import Termly from 'termly-namespace'

import Categories from 'constants/categories'

import CookieConsents, { CONSENT_LIFETIME_SECONDS } from 'utils/cookieConsents'


/**
 * @param {object} args
 * @param {string} args.consentMode
 * @param {object} args.cookieConsent
 *
 * @returns {boolean} true if consents have changed, if any consent is being
 *   flipped from true (accept) to false (reject), *and* either something in
 *   that category has already been allowed to load or nothing was watching
 *   the page closely enough for us to say it didn't. In 'opt_out' the choice
 *   also has to be storable, since a reload that forgets it stops nothing.
 */
export default function needsFullPageReload({ consentMode, cookieConsent }) {
  if ( CookieConsents.equals(cookieConsent) ) {
    return false
  }

  // In 'opt_out' a page that comes back without the visitor's choice starts
  // over as though they never made one, so the reload stops nothing. For a
  // GPC visitor it is worse, because the decline and the reload would repeat
  // on every arrival.
  if ( consentMode === 'opt_out' && !CookieConsents.canStoreConsents() ) {
    return false
  }

  const filterChangesRequiringReload = makeFilterForConsentMode(consentMode)

  // Releasing a manually-marked tag records what it was marked with, but only
  // the AutoBlocker sees anything the site maintainer did not mark. In
  // 'opt_out' the unmarked scripts have been running since the first paint,
  // so without the AutoBlocker an empty record cannot rule a load out and the
  // withdrawal has to be taken at its word.
  const isLoadRecordMissing = ( consentMode === 'opt_out' && !Termly.wasAutoBlockerInstalled() )

  return Object.entries(cookieConsent)
    // `do_not_sell` travels with the categories, but nothing loads under it.
    .filter(([key]) => Categories.has(key))
    .filter(filterChangesRequiringReload)
    .some(([category]) => {
      // A reload is the only way to stop code that is already running, so if
      // nothing in this category ever loaded, there is nothing to stop.
      return ( isLoadRecordMissing || Termly.hasLoadedCategory(category) )
    })
}

function makeFilterForConsentMode(consentMode) {
  if ( consentMode === 'opt_in' ) {
    const oldConsents = CookieConsents.getConsents() || {}

    // In 'opt_in' mode, nothing is loaded by default. So, if the user
    // rejects consent, there's nothing to do. If he accepts, we need
    // only unblock the scripts in situ--a reload is unnecessary.
    //
    // The only time we need to reload is if consent was once granted
    // but is now being declined. In that case, since we can't unload
    // running code, we have to reload the entire page.
    return ([category, isConsented]) => {
      return ( !isConsented && oldConsents[category] )
    }
  }

  const oldConsents = CookieConsents.getConsents(CONSENT_LIFETIME_SECONDS) || {}

  // In 'opt_out' mode, all scripts are loaded by default, so if the
  // user accepts, there is nothing to do. If he rejects consent, a
  // reload is required.
  return ([category, isConsented]) => {
    // Techically, all we really need here is `return !isConsented`,
    // but that would would require assuming that we're doing a
    // `CoookieConsents.equals()` check before we get here (otherwise,
    // going from "decline" to "decline" would result in a reload). I
    // don't want to make that assumption, so I'm doing the check
    // against the oldConsents objet. Paranoia is good.
    //
    // N.B.: I'm using `!== false` because in `opt_out`, an undefined
    // consent is the same as `true`. If we wanted to be really
    // verbose about this, we could say the following:
    // return ( !isConsented && (oldConsents[category] === void 0 || oldConsents[category] === true) )
    return ( !isConsented && (oldConsents[category] !== false) )
  }
}
